import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Eye, XCircle, RefreshCw, CheckCircle, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addDays, subDays, parseISO } from 'date-fns';
import { useProperty } from '@/contexts/PropertyContext';
import { todayInTz, formatDateTimeInTz } from '@/lib/propertyTime';
import { DuplicateBookingsNotice } from '@/components/DuplicateBookingsNotice';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper to get today's date in YYYY-MM-DD format
const getTodayString = (tz) => todayInTz(tz);

export default function ReservationsManager() {
  const { property } = useProperty();
  const propertyTz = property?.time_zone || 'America/New_York';
  const [properties, setProperties] = useState([]);
  const [areas, setAreas] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Date filter state - default to today (business date)
  const [dateFilterType, setDateFilterType] = useState('single'); // 'single', 'range', 'all'
  const [selectedDate, setSelectedDate] = useState(getTodayString(propertyTz));
  const [dateFrom, setDateFrom] = useState(getTodayString(propertyTz));
  const [dateTo, setDateTo] = useState(getTodayString(propertyTz));

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchAreas();
    }
    // Fetch reservations whenever filters change
    fetchReservations();
  }, [selectedProperty, selectedArea, statusFilter, dateFilterType, selectedDate, dateFrom, dateTo]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
      // Don't auto-select first property, allow "All Properties" view
      if (response.data.length > 0) {
        setSelectedProperty(''); // Start with all properties
      }
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`${API_URL}/areas?property_id=${selectedProperty}`);
      setAreas(response.data);
    } catch (error) {
      toast.error('Failed to load areas');
    }
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Only add filters if they have values
      if (selectedProperty) {
        params.append('property_id', selectedProperty);
      }
      if (selectedArea) {
        params.append('area_id', selectedArea);
      }
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // Add date filters
      if (dateFilterType === 'single') {
        // Single date: set both from and to as the same date
        params.append('date_from', selectedDate + 'T00:00:00');
        params.append('date_to', selectedDate + 'T23:59:59');
      } else if (dateFilterType === 'range') {
        params.append('date_from', dateFrom + 'T00:00:00');
        params.append('date_to', dateTo + 'T23:59:59');
      }
      // If 'all', don't add date filters

      const queryString = params.toString();
      const url = queryString ? `${API_URL}/reservations?${queryString}` : `${API_URL}/reservations`;
      
      const response = await axios.get(url);
      setReservations(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load reservations');
    } finally {
      setLoading(false);
    }
  };

  // Quick date navigation helpers
  const goToPreviousDay = () => {
    const prevDay = format(subDays(parseISO(selectedDate), 1), 'yyyy-MM-dd');
    setSelectedDate(prevDay);
  };

  const goToNextDay = () => {
    const nextDay = format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd');
    setSelectedDate(nextDay);
  };

  const goToToday = () => {
    setSelectedDate(getTodayString(propertyTz));
    setDateFrom(getTodayString(propertyTz));
    setDateTo(getTodayString(propertyTz));
  };

  const handleViewDetails = async (reservationId) => {
    try {
      const response = await axios.get(`${API_URL}/reservations/${reservationId}`);
      setSelectedReservation(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load reservation details');
    }
  };

  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm('Are you sure you want to cancel this reservation?')) return;

    try {
      await axios.post(`${API_URL}/reservations/${reservationId}/cancel`);
      toast.success('Reservation cancelled successfully');
      fetchReservations();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error('Failed to cancel reservation');
    }
  };

  const handleApproveReservation = async (reservationId) => {
    try {
      await axios.post(`${API_URL}/reservations/${reservationId}/approve`);
      toast.success('Reservation approved and confirmed');
      fetchReservations();
      // Refresh the detail view
      const response = await axios.get(`${API_URL}/reservations/${reservationId}`);
      setSelectedReservation(response.data);
    } catch (error) {
      toast.error('Failed to approve reservation');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-800',
      authorized: 'bg-blue-100 text-blue-800',
      pending_authorization: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      checked_in: 'bg-purple-100 text-purple-800',
      no_show: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout title="Reservations Management">
      <DuplicateBookingsNotice timeZone={propertyTz} refreshKey={reservations} />
      {/* Date Filter Section */}
      <div className="mb-6 bg-white border border-slate-200 rounded-md p-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#9F1239]" />
            <h3 className="font-serif font-medium text-lg">Date Filter</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={dateFilterType === 'single' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilterType('single')}
              className={dateFilterType === 'single' ? 'bg-[#9F1239] hover:bg-[#881133]' : ''}
              data-testid="filter-single-date"
            >
              Single Date
            </Button>
            <Button
              variant={dateFilterType === 'range' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilterType('range')}
              className={dateFilterType === 'range' ? 'bg-[#9F1239] hover:bg-[#881133]' : ''}
              data-testid="filter-date-range"
            >
              Date Range
            </Button>
            <Button
              variant={dateFilterType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilterType('all')}
              className={dateFilterType === 'all' ? 'bg-[#9F1239] hover:bg-[#881133]' : ''}
              data-testid="filter-all-dates"
            >
              All Dates
            </Button>
          </div>
        </div>

        {dateFilterType === 'single' && (
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousDay}
              data-testid="btn-prev-day"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="p-2 border border-[#E7E5E4] rounded-sm text-lg font-medium"
                data-testid="input-single-date"
              />
              <span className="text-[#78716C] text-sm">
                ({format(parseISO(selectedDate), 'EEEE')})
              </span>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextDay}
              data-testid="btn-next-day"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              data-testid="btn-today"
            >
              Today
            </Button>
          </div>
        )}

        {dateFilterType === 'range' && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="date-from" className="text-sm text-[#78716C]">From:</Label>
              <input
                id="date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="p-2 border border-[#E7E5E4] rounded-sm"
                data-testid="input-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="date-to" className="text-sm text-[#78716C]">To:</Label>
              <input
                id="date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="p-2 border border-[#E7E5E4] rounded-sm"
                data-testid="input-date-to"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              data-testid="btn-today-range"
            >
              Reset to Today
            </Button>
          </div>
        )}

        {dateFilterType === 'all' && (
          <p className="text-sm text-[#78716C]">Showing all reservations without date filter</p>
        )}
      </div>

      {/* Other Filters */}
      <div className="mb-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="property">Property</Label>
            <select
              id="property"
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
              data-testid="select-property"
            >
              {properties.map(prop => (
                <option key={prop.id} value={prop.id}>{prop.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="area">Area</Label>
            <select
              id="area"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
              data-testid="select-area"
            >
              <option value="">All Areas</option>
              {areas.map(area => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
              data-testid="select-status"
            >
              <option value="all">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="authorized">Authorized</option>
              <option value="pending_authorization">Pending Authorization</option>
              <option value="checked_in">Checked In</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No Show</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={fetchReservations}
              variant="outline"
              className="w-full"
              data-testid="btn-refresh"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#9F1239]" />
        </div>
      ) : reservations.length === 0 ? (
        <Card className="bg-white border-slate-200 shadow-sm rounded-md">
          <CardContent className="pt-6 text-center py-12">
            <p className="text-[#78716C]">No reservations found with the current filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reservations.map((reservation) => (
            <Card key={reservation.id} className="bg-white border-slate-200 shadow-sm rounded-md" data-testid={`reservation-${reservation.id}`}>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                  <div className="md:col-span-2">
                    <p className="text-sm text-[#78716C]">Guest</p>
                    <p className="font-serif font-medium text-lg">{reservation.guest_name}</p>
                    <p className="text-sm text-[#78716C]">{reservation.guest_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">Seat / Room</p>
                    <p className="font-medium">{reservation.inventory_label}</p>
                    <p className="text-sm text-[#78716C]" data-testid={`reservation-room-${reservation.id}`}>
                      Room {reservation.room_number || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">Date</p>
                    <p className="font-medium">
                      {new Date(reservation.reservation_date.slice(0, 10) + 'T12:00:00').toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">Status</p>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(reservation.status)}`}>
                      {reservation.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(reservation.id)}
                      data-testid={`btn-view-${reservation.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Details Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">Reservation Details</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#78716C]">Confirmation Code</p>
                  <p className="font-serif font-bold text-xl text-[#9F1239]">
                    {selectedReservation.confirmation_code}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#78716C]">Status</p>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedReservation.status)}`}>
                    {selectedReservation.status.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-serif font-medium text-lg mb-3">Guest Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-[#78716C]">Name</p>
                    <p className="font-medium">{selectedReservation.guest_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">Email</p>
                    <p className="font-medium">{selectedReservation.guest_email}</p>
                  </div>
                  {selectedReservation.guest_phone && (
                    <div>
                      <p className="text-sm text-[#78716C]">Phone</p>
                      <p className="font-medium">{selectedReservation.guest_phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-[#78716C]">Guest Type</p>
                    <p className="font-medium capitalize">{selectedReservation.guest_type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">{selectedReservation.guest_type === 'resident' ? 'Unit Number' : 'Room Number'}</p>
                    <p className="font-medium" data-testid="detail-room-number">{selectedReservation.room_number || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">Booked At</p>
                    <p className="font-medium" data-testid="detail-booked-at">{formatDateTimeInTz(selectedReservation.created_at, propertyTz)}</p>
                  </div>
                  {selectedReservation.hotel_confirmation_code && (
                    <div>
                      <p className="text-sm text-[#78716C]">Hotel Confirmation</p>
                      <p className="font-medium" data-testid="detail-hotel-confirmation">{selectedReservation.hotel_confirmation_code}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-serif font-medium text-lg mb-3">Reservation Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-[#78716C]">Seat/Item</p>
                    <p className="font-medium">{selectedReservation.inventory_label}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">Date</p>
                    <p className="font-medium">
                      {new Date(selectedReservation.reservation_date.slice(0, 10) + 'T12:00:00').toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">Adults</p>
                    <p className="font-medium">{selectedReservation.adults}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C]">Amount</p>
                    <p className="font-serif font-bold text-lg text-[#9F1239]">
                      {selectedReservation.amount > 0 ? `$${selectedReservation.amount}` : 'FREE'}
                    </p>
                  </div>
                </div>
              </div>

              {selectedReservation.payment_authorization_id && (
                <div className="border-t pt-4">
                  <h3 className="font-serif font-medium text-lg mb-3">Payment Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#78716C]">Authorization Status</p>
                      <p className="font-medium">
                        {selectedReservation.payment_captured ? 'Captured' : 'Authorized Only'}
                      </p>
                    </div>
                    {selectedReservation.payment_captured_at && (
                      <div>
                        <p className="text-sm text-[#78716C]">Captured At</p>
                        <p className="font-medium">
                          {formatDateTimeInTz(selectedReservation.payment_captured_at, propertyTz)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t pt-4 flex gap-3">
                {selectedReservation.status === 'pending_authorization' && (
                  <Button
                    onClick={() => handleApproveReservation(selectedReservation.id)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    data-testid="btn-approve-reservation"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Approve Reservation
                  </Button>
                )}
                {selectedReservation.status !== 'cancelled' && (
                  <Button
                    onClick={() => handleCancelReservation(selectedReservation.id)}
                    variant="destructive"
                    className="flex-1"
                    data-testid="btn-cancel-reservation"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Cancel Reservation
                  </Button>
                )}
                <Button
                  onClick={() => setDetailDialogOpen(false)}
                  variant="outline"
                  className="flex-1"
                  data-testid="btn-close"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
